import { EDITOR_WORKFLOW_PAGES } from "./editorWorkflowContent";

export type EditorContentKind = "feature" | "comparison" | "guide" | "template" | "security";

export type EditorContentSection = {
  heading: string;
  paragraphs: string[];
  bullets?: string[];
};

export type EditorComparisonRow = {
  question: string;
  novilot: string;
  other: string;
};

export type EditorTemplateSection = {
  heading: string;
  note: string;
  fields: string[];
};

export type EditorContentPage = {
  kind: EditorContentKind;
  slug: string;
  path: string;
  title: string;
  description: string;
  keywords: string[];
  heading: string;
  lede: string;
  summary: string;
  sections: EditorContentSection[];
  comparisonName?: string;
  comparisonRows?: EditorComparisonRow[];
  sourceUrl?: string;
  sourceLabel?: string;
  additionalSources?: { href: string; label: string }[];
  datePublished?: string;
  templateSections?: EditorTemplateSection[];
  related: { href: string; label: string }[];
  ctaTitle: string;
  ctaBody: string;
};

const sharedRelated = [
  { href: "/for/fiction-editors", label: "Novilot for fiction editors" },
  { href: "/sample-continuity-report", label: "Synthetic sample continuity report" },
  { href: "/security/editor-data-processing", label: "Client manuscript data and AI processing" },
];

export const EDITOR_FEATURE_PAGES: EditorContentPage[] = [
  {
    kind: "feature",
    slug: "editorial-preflight",
    path: "/features/editorial-preflight",
    title: "Editorial Preflight for Fiction Editors - Novilot",
    description:
      "Use Novilot for an evidence-backed fiction manuscript preflight: import the draft, run continuity and targeted story checks, then review every finding yourself.",
    keywords: [
      "editorial preflight",
      "manuscript preflight",
      "manuscript analysis software for editors",
      "developmental editing tools",
    ],
    heading: "Begin the edit with an evidence-backed manuscript preflight",
    lede:
      "Novilot gives fiction editors a structured first pass across a long manuscript. It surfaces possible continuity and story problems with source context; it does not decide what belongs in the editorial letter.",
    summary:
      "Best for developmental editors, manuscript evaluators, book coaches, and continuity specialists who want a systematic second read before writing their own recommendations.",
    sections: [
      {
        heading: "What the preflight is for",
        paragraphs: [
          "A professional edit begins with orientation: what the manuscript establishes, where its pressure points are, and which questions deserve closer reading. Novilot can support that stage by reading across chapters and organizing possible issues before the editor commits them to a client deliverable.",
          "Treat the output as a review queue. Confirm the cited passages, reject false positives, mark intentional differences, and translate only useful findings into your own editorial language.",
        ],
      },
      {
        heading: "A practical current workflow",
        paragraphs: [
          "Import the client manuscript, verify the chapter split, build or approve useful story-bible context, then run Continuity Guardian. Follow with the targeted analyses that match the agreed scope: structure, pacing, character, dialogue, point of view, or prose patterns.",
        ],
        bullets: [
          "Keep the original client file as the source of truth.",
          "Record what portion of the manuscript each scan covered.",
          "Review cited evidence before using a finding.",
          "Carry selected observations into your existing editorial letter or query workflow.",
        ],
      },
      {
        heading: "What Novilot does not replace",
        paragraphs: [
          "The preflight is not a complete developmental edit, fact check, sensitivity read, copyedit, or proofread. It cannot understand every authorial intention, and it may miss issues or flag details that are deliberately different.",
          "The editor workspace includes approved queries, expiring client review links, revision comparison, and report exports. Its Word handoff adds comments and explicitly approved simple replacements to an original DOCX; complex edits still need review in Word. It is not a complete Word round trip or native add-in.",
        ],
      },
    ],
    related: [
      { href: "/features/fiction-continuity-audit", label: "Fiction continuity audit" },
      { href: "/how-to/run-a-fiction-continuity-pass", label: "How to run a continuity pass" },
      ...sharedRelated,
    ],
    ctaTitle: "Run an editor-controlled preflight",
    ctaBody: "Start with a client-authorized manuscript and verify every finding before it enters your work.",
  },
  {
    kind: "feature",
    slug: "fiction-continuity-audit",
    path: "/features/fiction-continuity-audit",
    title: "Fiction Continuity Audit for Editors - Novilot",
    description:
      "Audit a novel for possible character, timeline, relationship, and world-rule contradictions. Review grounded manuscript evidence and scan coverage in Novilot.",
    keywords: [
      "continuity checker for editors",
      "fiction continuity audit",
      "manuscript consistency checker",
      "novel contradiction checker",
    ],
    heading: "Trace possible continuity breaks back to both passages",
    lede:
      "A vague warning creates more work. Novilot’s continuity findings connect the later detail to the earlier manuscript evidence so an editor can decide whether the conflict is real, intentional, or unresolved.",
    summary:
      "Use the audit for character traits, chronology, relationships, possessions, location facts, world rules, and other details that can drift across a long draft.",
    sections: [
      {
        heading: "Evidence before interpretation",
        paragraphs: [
          "Every useful continuity question begins with what the manuscript actually says. A finding should identify the chapters, quote the relevant lines, explain the possible disagreement, and leave room for context the scan may not understand.",
          "That evidence-first structure lets an editor reject weak findings quickly and spend attention on contradictions that warrant a note or author query.",
        ],
      },
      {
        heading: "Common continuity categories",
        paragraphs: [
          "Continuity is broader than eye color. In complex fiction, editors often need to trace who knew what, whether enough time passed, whether an object changed hands, and whether a later solution violates an earlier world rule.",
        ],
        bullets: [
          "Character appearance, age, injury, name, role, or allegiance",
          "Dates, seasons, travel time, event order, and elapsed time",
          "Relationships, meetings, promises, secrets, and knowledge states",
          "Locations, possessions, access rules, technology, or magic constraints",
        ],
      },
      {
        heading: "The editor remains the adjudicator",
        paragraphs: [
          "Two passages can differ without contradicting each other. A character may lie, a narrator may be unreliable, a rule may have an exception, or the story may intentionally withhold context. Novilot should raise the question; the editor determines the reading.",
        ],
      },
    ],
    related: [
      { href: "/templates/fiction-continuity-checklist", label: "Fiction continuity checklist" },
      { href: "/how-to/track-character-knowledge", label: "Track what each character knows" },
      ...sharedRelated,
    ],
    ctaTitle: "Inspect a whole-manuscript continuity finding",
    ctaBody: "Import a manuscript, run Continuity Guardian, and decide what the cited passages mean.",
  },
  {
    kind: "feature",
    slug: "manuscript-analysis-for-editors",
    path: "/features/manuscript-analysis-for-editors",
    title: "Manuscript Analysis Tools for Fiction Editors - Novilot",
    description:
      "Explore manuscript analysis tools for fiction editors covering structure, pacing, character, dialogue, point of view, continuity, and prose patterns.",
    keywords: [
      "manuscript analysis tools for editors",
      "fiction editing software for editors",
      "developmental editing software",
      "structural editing tools",
    ],
    heading: "Use analysis as a reading aid, not as the editorial verdict",
    lede:
      "Novilot includes a broad catalog of fiction analyses. The professional use is selective: choose the checks that fit the agreed edit, inspect their evidence, and keep your own hierarchy of what matters.",
    summary:
      "The strongest editor workflow is not running every tool. It is combining a continuity audit with a small set of targeted reads that answer the manuscript’s actual questions.",
    sections: [
      {
        heading: "Choose checks from the editorial scope",
        paragraphs: [
          "A manuscript evaluation may need structure, pacing, character arc, point of view, and continuity. A focused continuity pass may need only canon, chronology, character knowledge, and world rules. Running analyses outside the contracted scope creates noise and can blur the value of the editor’s deliverable.",
        ],
      },
      {
        heading: "Separate signals from recommendations",
        paragraphs: [
          "A repeated dialogue tag, a long scene, or a late character entrance is a signal. Whether it is a problem depends on genre, voice, scene purpose, and the author’s goals. Use tool output to locate patterns, then write recommendations in the language of the manuscript and the edit.",
        ],
      },
      {
        heading: "Build a small repeatable pass",
        paragraphs: [
          "Start with a core sequence you can repeat across clients, then save specialized checks for the manuscripts that need them. A compact preflight is easier to evaluate, price, and improve than a shelf of disconnected scores.",
        ],
        bullets: [
          "Continuity and canon",
          "Structure and pacing",
          "Character and point of view",
          "Dialogue or prose patterns only when in scope",
        ],
      },
    ],
    related: [
      { href: "/features/editorial-preflight", label: "Editorial preflight workflow" },
      { href: "/features/analysis-tools", label: "Complete analysis-tool catalog" },
      ...sharedRelated,
    ],
    ctaTitle: "Build a focused manuscript preflight",
    ctaBody: "Start with continuity, then add only the analyses that match your editorial scope.",
  },
  {
    kind: "feature",
    slug: "story-bible-for-editors",
    path: "/features/story-bible-for-editors",
    title: "Story Bible Software for Fiction Editors - Novilot",
    description:
      "Use an approve-first story bible to organize characters, locations, world rules, and manuscript facts before checking continuity across a client novel.",
    keywords: [
      "story bible software for editors",
      "editor story reference software",
      "character bible for editors",
      "novel canon tracker",
    ],
    heading: "Turn manuscript facts into an editor-reviewed reference",
    lede:
      "Novilot can suggest story-bible entries from a manuscript, but suggestions do not become canon automatically. Review what is useful, correct what is incomplete, and use the approved reference to support continuity work.",
    summary:
      "A Novilot story bible complements, but does not replace, a professional copyediting style sheet. It is strongest for narrative entities and story facts across a long novel or series.",
    sections: [
      {
        heading: "Suggest, then approve",
        paragraphs: [
          "Automatic extraction is a starting point. Names, aliases, relationships, locations, objects, and world rules still need human review because fiction frequently contains uncertainty, deception, and changing beliefs.",
        ],
      },
      {
        heading: "Use the bible as context, not authority",
        paragraphs: [
          "The manuscript remains the primary evidence. When the story bible and the prose disagree, return to the source passages and decide whether the reference is stale, the draft has drifted, or the difference is intentional.",
        ],
      },
      {
        heading: "Know the boundary with a style sheet",
        paragraphs: [
          "The story bible records narrative entities and canon. The separate editor style sheet records approved terms, variants, and editorial rules, with a free literal term check. Neither replaces a comprehensive copyediting tool or your agreed house style.",
        ],
      },
    ],
    related: [
      { href: "/features/story-bible", label: "Story Bible feature" },
      { href: "/features/fiction-editor-style-sheet", label: "Approved fiction style sheets" },
      { href: "/how-to/check-character-consistency-across-a-novel", label: "Check character consistency" },
      ...sharedRelated,
    ],
    ctaTitle: "Build an approved manuscript reference",
    ctaBody: "Import a novel, review suggested entries, and keep human control over what becomes canon.",
  },
];

export const EDITOR_COMPARISON_PAGES: EditorContentPage[] = [
  {
    kind: "comparison",
    slug: "novilot-vs-fictionary",
    path: "/compare/novilot-vs-fictionary",
    title: "Novilot vs Fictionary StoryCoach for Fiction Editors",
    description:
      "Compare Novilot and Fictionary StoryCoach for professional fiction editing: continuity evidence, structural editing, client workflow, comments, and manuscript handoff.",
    keywords: ["Novilot vs Fictionary", "Fictionary alternative for editors", "StoryCoach alternative"],
    heading: "Novilot vs Fictionary StoryCoach",
    lede:
      "Fictionary StoryCoach centers on a structured scene-level editing method. Novilot centers on evidence review, approved author queries, and revision follow-up across client manuscripts. Choose around the part of your editorial process that needs support.",
    summary:
      "Choose Fictionary when scene-level structural method, track changes, comments, and client management are central. Evaluate Novilot when dual-passage continuity evidence and a broader manuscript-analysis workspace are the priority.",
    comparisonName: "Fictionary StoryCoach",
    sourceUrl: "https://fictionary.co/products/storycoach/",
    sourceLabel: "Fictionary’s official StoryCoach page",
    comparisonRows: [
      { question: "Primary audience", novilot: "Fiction editors in a dedicated workspace, alongside author tools", other: "Professional structural editors" },
      { question: "Core strength", novilot: "Whole-manuscript continuity evidence and fiction analysis", other: "Structured scene analysis and editor/client workflow" },
      { question: "Track changes and comments", novilot: "DOCX comments and approved simple replacements; not a complete Word round trip", other: "Advertises track changes, comments, notes, and scene-by-scene editorial notes" },
      { question: "Client management", novilot: "Multi-client desk, project collaborators, approved queries, and revision follow-up", other: "Advertises a client dashboard and work across multiple manuscripts" },
      { question: "Continuity evidence", novilot: "Grounded passages and chapter references, with editor review decisions", other: "Structural insights and scene notes are the product’s clearer emphasis" },
    ],
    sections: [
      {
        heading: "Where Fictionary is stronger today",
        paragraphs: [
          "StoryCoach is explicitly built and marketed for professional editors. Its official product page describes scene notes, structural visuals, prewritten educational comments, track changes, comments, and a client dashboard. Editors seeking a complete structural method and established professional ecosystem should evaluate those advantages directly.",
        ],
      },
      {
        heading: "Where Novilot is meaningfully different",
        paragraphs: [
          "Novilot’s continuity workflow centers on the relationship between two passages that may disagree. The finding is useful only when the editor can inspect both pieces of manuscript evidence and decide whether the difference is a real error, an intentional choice, or an author query.",
        ],
      },
      {
        heading: "A practical decision",
        paragraphs: [
          "Evaluate Fictionary for its scene-level method and structural visuals. Test Novilot when continuity review and the loop from query to client response to revised draft create the most manual bookkeeping. Neither a feature table nor an AI result replaces testing an authorized sample in your own delivery process.",
        ],
      },
    ],
    related: [{ href: "/features/fiction-continuity-audit", label: "Novilot continuity audit" }, ...sharedRelated],
    ctaTitle: "Compare the actual evidence workflow",
    ctaBody: "Open the synthetic report, then decide whether dual-passage continuity belongs in your edit.",
  },
  {
    kind: "comparison",
    slug: "novilot-vs-plotlens",
    path: "/compare/novilot-vs-plotlens",
    title: "Novilot vs PlotLens for Fiction Editors",
    description:
      "Compare Novilot and PlotLens for fiction continuity: manuscript evidence, canon, editor workflow, reports, integrations, and team features.",
    keywords: ["Novilot vs PlotLens", "PlotLens alternative", "continuity software for editors"],
    heading: "Novilot vs PlotLens",
    lede:
      "Both tools offer manuscript continuity checks with evidence and human review. PlotLens emphasizes narrative validation and connections to writing environments. Novilot has a dedicated editor desk for client intake, approved queries, responses, and revision follow-up.",
    summary:
      "Evaluate PlotLens if an in-Word validation workflow matters most. Evaluate Novilot if managing the review-to-client-to-revision loop is the priority. Test both with the same authorized sample; no comparative accuracy benchmark is claimed here.",
    comparisonName: "PlotLens",
    sourceUrl: "https://plotlens.ai/for/editors/",
    sourceLabel: "PlotLens’s official editors and publishers page",
    additionalSources: [{ href: "https://plotlens.ai/integrations/", label: "PlotLens integration availability" }],
    comparisonRows: [
      { question: "Continuity evidence", novilot: "Grounded passages and chapter references, with editor review decisions", other: "Severity, context, source citations, and exact passages" },
      { question: "Canon", novilot: "Suggested entries reviewed by the user", other: "Story bibles and derived rules for human acceptance or rejection" },
      { question: "Editor workflow", novilot: "Multi-client desk, editor/reviewer access, approved queries, client responses, and revision comparison", other: "Separate author projects and exported reports; its editor page says there is no editor-specific mode" },
      { question: "Integrations", novilot: "File import/export; no Word or Docs add-in", other: "Word add-in; Scrivener companion in beta; Google Docs marked coming soon" },
      { question: "Broader workspace", novilot: "Planning, drafting, analysis, story bible, beta reading, and publishing surfaces", other: "Narrative intelligence and validation are the sharper focus" },
    ],
    sections: [
      {
        heading: "Where PlotLens fits",
        paragraphs: [
          "PlotLens provides manuscript checks and cited reports. Its integrations page lists a Word add-in, beta Scrivener support, and Google Docs as coming soon. Check current availability before starting client work.",
        ],
      },
      {
        heading: "Where Novilot fits",
        paragraphs: [
          "Novilot keeps client intake, permission records, evidence decisions, approved style sheets, private notes, client responses, and revision comparison in the editor workflow. Its DOCX handoff supports comments and approved simple tracked replacements, but it is not an in-Word add-in or a complete Word round trip.",
        ],
      },
      {
        heading: "How to compare fairly",
        paragraphs: [
          "Use the same small, authorized manuscript fixture in both products. Compare coverage disclosure, false positives, passage quality, time to dismiss a weak finding, report usability, privacy controls, and the steps required to return the result to the author.",
        ],
      },
    ],
    related: [{ href: "/how-to/run-a-fiction-continuity-pass", label: "Continuity-pass evaluation workflow" }, ...sharedRelated],
    ctaTitle: "Evaluate Novilot on the same evidence",
    ctaBody: "Use a controlled manuscript sample and inspect what each product cites, misses, and asks you to decide.",
  },
  {
    kind: "comparison",
    slug: "novilot-and-perfectit",
    path: "/compare/novilot-and-perfectit",
    title: "Novilot and PerfectIt: A Fiction Editor Workflow",
    description:
      "See how Novilot and PerfectIt can support different fiction editing passes: narrative continuity across a novel and mechanical consistency inside Word.",
    keywords: ["Novilot and PerfectIt", "PerfectIt for fiction", "PerfectIt alternative fiction continuity"],
    heading: "Novilot and PerfectIt solve different consistency problems",
    lede:
      "PerfectIt is a professional Word-based consistency assistant. Novilot looks for narrative facts and relationships that drift across a novel. For many editors, the useful question is not which one replaces the other—it is where each belongs in the edit.",
    summary:
      "Use PerfectIt for spelling, capitalization, hyphenation, terminology, and style-sheet consistency in Word. Evaluate Novilot for character, timeline, knowledge, relationship, possession, and world-rule continuity across fiction.",
    comparisonName: "PerfectIt",
    sourceUrl: "https://www.perfectit.com/roles-overview",
    sourceLabel: "PerfectIt’s official roles overview",
    comparisonRows: [
      { question: "Primary environment", novilot: "Web manuscript workspace", other: "Professional consistency workflow centered on Word" },
      { question: "Consistency type", novilot: "Narrative canon and cross-chapter story facts", other: "Mechanical, terminology, spelling, capitalization, and house style" },
      { question: "Style sheets", novilot: "Approved term rules, reusable templates, and a free literal term check; not full mechanical copyediting", other: "Customizable and shareable style sheets are a core strength" },
      { question: "Human control", novilot: "The user reviews every finding", other: "Explicitly leaves every editing decision to the human" },
      { question: "Best combined sequence", novilot: "Narrative continuity before or during developmental review", other: "Mechanical consistency during copyedit and final QA" },
    ],
    sections: [
      {
        heading: "Run the narrative pass first",
        paragraphs: [
          "Use Novilot when you need to trace story facts across distant chapters: what a character knows, when an object changes hands, whether travel time works, or whether a later solution violates an earlier rule. Confirm the evidence and raise author queries before the text is mechanically polished.",
        ],
      },
      {
        heading: "Run the mechanical pass in Word",
        paragraphs: [
          "PerfectIt’s stated strength is locating consistency problems and applying style guidance without taking the editing decision away from the professional. That makes it better suited to the copyediting and quality-control work Novilot does not currently replace.",
        ],
      },
      {
        heading: "Keep one style-sheet authority",
        paragraphs: [
          "Do not let the story bible, Word style sheet, and query log become three conflicting authorities. Decide which artifact controls names, capitalization, numbers, punctuation, and house style; use Novilot’s story context as evidence rather than duplicating every copyediting decision.",
        ],
      },
    ],
    related: [{ href: "/features/story-bible-for-editors", label: "Story bible boundary for editors" }, ...sharedRelated],
    ctaTitle: "Add a narrative continuity pass",
    ctaBody: "Keep PerfectIt for its professional Word strengths and test Novilot on long-range story facts.",
  },
  {
    kind: "comparison",
    slug: "novilot-vs-autocrit",
    path: "/compare/novilot-vs-autocrit",
    title: "Novilot vs AutoCrit for Fiction Editors",
    description:
      "Compare Novilot and AutoCrit for fiction analysis, continuity, contradictory events, timeline review, prose reports, drafting, and professional editor use.",
    keywords: ["Novilot vs AutoCrit", "AutoCrit for editors", "AutoCrit alternative"],
    heading: "Novilot vs AutoCrit",
    lede:
      "AutoCrit is a broad writer-first platform with prose reports, Story Analyzer+, beta readers, planning, and publishing tools. Novilot is also broad, but its clearest differentiator is continuity findings that connect the passages that may disagree.",
    summary:
      "Choose AutoCrit when genre-based self-editing reports and a writer community are central. Evaluate Novilot when an evidence-led continuity pass is the primary job.",
    comparisonName: "AutoCrit",
    sourceUrl: "https://www.autocrit.com/",
    sourceLabel: "AutoCrit’s official product overview",
    comparisonRows: [
      { question: "Primary audience", novilot: "Fiction editors in a dedicated workspace, alongside author tools", other: "Primarily authors and self-editors" },
      { question: "Analysis breadth", novilot: "Continuity plus structure, character, dialogue, prose, and publishing tools", other: "Prose reports, Story Analyzer+, AI readers, planning, and publishing tools" },
      { question: "Continuity", novilot: "Grounded passages and chapter references, with editor review decisions", other: "Story and series analysis with iterative draft feedback" },
      { question: "Line-level work", novilot: "Proofread and prose-analysis surfaces", other: "Extensive prose, readability, repetition, dialogue, and genre comparison reports" },
      { question: "Professional handoff", novilot: "Approved client queries, expiring review links, reports, and limited original-DOCX handoff", other: "Advertises collaboration with writing partners and editors" },
    ],
    sections: [
      {
        heading: "Where AutoCrit is stronger",
        paragraphs: [
          "AutoCrit combines prose reports, genre comparisons, Story Analyzer+, AI readers, community, planning, and publishing tools. It also advertises collaboration and revision-aware feedback. A writer-centered workspace can still be useful to an editor; do not assume it lacks those capabilities.",
        ],
      },
      {
        heading: "Where Novilot is sharper",
        paragraphs: [
          "Novilot’s continuity experience is designed around verification. The relevant question is not only that a contradiction may exist, but which two passages establish the disagreement and what decision the human makes after reading them.",
        ],
      },
      {
        heading: "For professional editors",
        paragraphs: [
          "Test Novilot’s client query and revision loop as well as its analysis. Check what is shared, how private notes are protected, and how a revised manuscript reaches your desk. Review any exported DOCX in Word before delivering it; the handoff supports a limited set of edits, not every Word workflow.",
        ],
      },
    ],
    related: [{ href: "/features/manuscript-analysis-for-editors", label: "Novilot manuscript analysis for editors" }, ...sharedRelated],
    ctaTitle: "Inspect the continuity evidence directly",
    ctaBody: "See how Novilot connects two passages before deciding which analysis workflow fits your edit.",
  },
  {
    kind: "comparison",
    slug: "novilot-vs-prowritingaid",
    path: "/compare/novilot-vs-prowritingaid",
    title: "Novilot vs ProWritingAid for Fiction Editors",
    description:
      "Compare Novilot and ProWritingAid for fiction editing: continuity, manuscript analysis, prose reports, style guides, terminology, collaboration, and integrations.",
    keywords: ["Novilot vs ProWritingAid", "ProWritingAid for editors", "ProWritingAid manuscript analysis alternative"],
    heading: "Novilot vs ProWritingAid",
    lede:
      "ProWritingAid offers extensive writing reports, grammar and style assistance, integrations, and story critiques. Novilot combines manuscript evidence review with a dedicated client-query and revision workflow for fiction editors.",
    summary:
      "Choose ProWritingAid for integrations, line-level reports, terminology, and custom style guidance. Evaluate Novilot when fiction canon and long-range continuity are the primary problem.",
    comparisonName: "ProWritingAid",
    sourceUrl: "https://prowritingaid.com/features/writing-reports",
    sourceLabel: "ProWritingAid’s official writing reports",
    additionalSources: [{ href: "https://prowritingaid.com/", label: "ProWritingAid’s official product overview" }],
    comparisonRows: [
      { question: "Prose analysis", novilot: "Fiction-focused proofread and analysis catalog", other: "25+ reports, grammar, style, readability, terminology, and custom style guide" },
      { question: "Whole-story analysis", novilot: "Continuity Guardian and multiple manuscript analyses", other: "Manuscript, plot, character, virtual beta-reader, and marketability analyses" },
      { question: "Continuity evidence", novilot: "Grounded passages and chapter references, with editor review decisions", other: "Advanced analysis output; verify current report format for the exact use case" },
      { question: "Workflow", novilot: "Approved client queries, private notes, responses, and revision comparison", other: "Reports in its web editor, browser extensions, and Desktop Everywhere" },
      { question: "Best fit", novilot: "Narrative continuity and story context", other: "Integrated drafting, line editing, style control, and broad writer workflow" },
    ],
    sections: [
      {
        heading: "Where ProWritingAid is stronger",
        paragraphs: [
          "ProWritingAid lists more than 25 reports, including grammar, style, repetitions, readability, consistency, and house style. Its web editor, extensions, and Desktop Everywhere support an integrated line-editing workflow. Its separate story-critique tools also cover manuscript, plot, and character analysis.",
        ],
      },
      {
        heading: "Where Novilot is more specific",
        paragraphs: [
          "Novilot’s strongest claim is narrower: a possible fiction continuity problem should be inspectable as a relationship between passages, not only as a score or general recommendation. That makes the tool most relevant where canon bookkeeping becomes the bottleneck.",
        ],
      },
      {
        heading: "Check the total manuscript cost",
        paragraphs: [
          "Novilot shows a credit quote before an AI preflight. ProWritingAid documents separate limits for some advanced reports. Check current plans and calculate the manuscript length and expected revision rounds before committing either tool to a fixed-fee engagement.",
        ],
      },
    ],
    related: [{ href: "/features/fiction-continuity-audit", label: "Novilot continuity evidence" }, ...sharedRelated],
    ctaTitle: "Test the continuity-specific workflow",
    ctaBody: "Use the sample report to decide whether dual-passage evidence fills a gap in your existing editing stack.",
  },
];

export const EDITOR_GUIDE_PAGES: EditorContentPage[] = [
  {
    kind: "guide",
    slug: "run-a-fiction-continuity-pass",
    path: "/how-to/run-a-fiction-continuity-pass",
    title: "How to Run a Fiction Continuity Pass",
    description:
      "A practical continuity-editing workflow for checking characters, timelines, knowledge, relationships, objects, and world rules across a novel.",
    keywords: ["how to check continuity in a novel", "fiction continuity pass", "novel continuity checklist"],
    heading: "How to run a fiction continuity pass",
    lede:
      "A continuity pass is not a search for every repeated noun. It is a controlled review of the facts, states, and rules readers must be able to reconcile across the story.",
    summary:
      "Start with scope and canon, review high-risk categories, record both passages, adjudicate context, then turn only verified issues into author queries or recommendations.",
    sections: [
      {
        heading: "Define what this pass covers",
        paragraphs: [
          "Agree whether the pass covers one manuscript, a series, or only revised chapters. Record the draft version and decide whether you are checking internal story continuity, real-world factual accuracy, or both. They are different jobs.",
        ],
      },
      {
        heading: "Build a risk-led canon",
        paragraphs: [
          "Prioritize facts likely to cause reader-visible problems: character traits and injuries, dates and travel time, who possesses key objects, who knows each secret, relationship changes, access constraints, and magic or technology rules.",
        ],
      },
      {
        heading: "Record the evidence pair",
        paragraphs: [
          "For every suspected contradiction, record both passages, their locations, the exact fact that seems incompatible, and any contextual explanation. A note without the earlier evidence forces the author to repeat the editor’s search.",
        ],
      },
      {
        heading: "Adjudicate before reporting",
        paragraphs: [
          "Mark each item confirmed, intentional, ambiguous, stale-canon, false positive, or query author. Consolidate duplicate findings and separate consequential contradictions from harmless wording differences.",
        ],
      },
    ],
    related: [{ href: "/templates/fiction-continuity-checklist", label: "Copy the continuity checklist" }, ...sharedRelated],
    ctaTitle: "Add manuscript evidence to the pass",
    ctaBody: "Use Novilot to surface possible evidence pairs, then keep the professional judgment yours.",
  },
  {
    kind: "guide",
    slug: "check-character-consistency-across-a-novel",
    path: "/how-to/check-character-consistency-across-a-novel",
    title: "How to Check Character Consistency Across a Novel",
    description:
      "Check character facts, motivations, relationships, injuries, possessions, voice, and knowledge states across chapters without confusing change with inconsistency.",
    keywords: ["character consistency checker", "check character consistency novel", "character continuity editing"],
    heading: "How to check character consistency across a novel",
    lede:
      "A character can change dramatically and remain consistent. The editor’s job is to distinguish earned development, deliberate deception, and point-of-view limits from accidental factual drift.",
    summary:
      "Track stable facts separately from changing states, connect every question to passages, and evaluate the change against the story’s chronology and point of view.",
    sections: [
      {
        heading: "Separate identity from state",
        paragraphs: [
          "Identity facts include names, age, appearance, family, history, and durable abilities. State includes location, injury, allegiance, possessions, mood, goals, and current knowledge. State is expected to change; the manuscript must show how and when.",
        ],
      },
      {
        heading: "Track relationships and knowledge",
        paragraphs: [
          "Many character problems are relational rather than individual. Record when two people meet, what they call each other, what promises they make, and which facts each person could reasonably know at that point in the story.",
        ],
      },
      {
        heading: "Check the causal bridge",
        paragraphs: [
          "When two passages differ, look for the scene that bridges them. A missing bridge may be a continuity error, an underdeveloped arc, or an intentional revelation that needs clearer staging.",
        ],
      },
      {
        heading: "Write the query without deciding for the author",
        paragraphs: [
          "State what the earlier and later passages establish, explain why readers may struggle to reconcile them, and ask whether the difference is intentional. Avoid prescribing a rewrite until the narrative intention is clear.",
        ],
      },
    ],
    related: [{ href: "/features/story-bible-for-editors", label: "Story bible for editors" }, ...sharedRelated],
    ctaTitle: "Trace character facts to the manuscript",
    ctaBody: "Use story-bible context and cited continuity findings as a second read—not a substitute for character analysis.",
  },
  {
    kind: "guide",
    slug: "track-character-knowledge",
    path: "/how-to/track-character-knowledge",
    title: "How to Track What Each Character Knows",
    description:
      "Build a character knowledge timeline for secrets, discoveries, lies, beliefs, and revelations so dialogue and decisions remain plausible across a novel.",
    keywords: ["track what each character knows", "character knowledge timeline", "fiction knowledge continuity"],
    heading: "How to track what each character knows",
    lede:
      "Knowledge continuity asks a precise question: at this point in the story, what information could this character possess, and why do they believe it?",
    summary:
      "Track facts, beliefs, lies, sources, witnesses, and discovery scenes separately. A character can act on a false belief consistently even when the reader knows the truth.",
    sections: [
      {
        heading: "Create one entry per meaningful fact",
        paragraphs: [
          "For each secret or revelation, record the objective story fact, the characters who know it, the characters who believe a false version, and the scenes where those states change.",
        ],
      },
      {
        heading: "Record the information path",
        paragraphs: [
          "A character learns through observation, testimony, inference, memory, a document, or supernatural/technical access. The path matters because it controls how certain they should be and which details they can repeat accurately.",
        ],
      },
      {
        heading: "Audit dialogue and decisions",
        paragraphs: [
          "Review scenes where a secret drives action. Look for unexplained familiarity, premature names, impossible conclusions, and dialogue that assumes information never shared on the page.",
        ],
      },
      {
        heading: "Respect viewpoint and unreliable narration",
        paragraphs: [
          "Do not normalize every belief into omniscient canon. Point of view, deception, memory, and bias may require multiple versions of the same event. Report the knowledge-state mismatch, not merely the difference in wording.",
        ],
      },
    ],
    related: [{ href: "/features/fiction-continuity-audit", label: "Continuity audit for editors" }, ...sharedRelated],
    ctaTitle: "Check knowledge across distant chapters",
    ctaBody: "Surface possible knowledge conflicts with evidence, then evaluate the information path yourself.",
  },
  {
    kind: "guide",
    slug: "use-ai-with-client-manuscripts",
    path: "/how-to/use-ai-with-client-manuscripts",
    title: "How Fiction Editors Can Use AI With Client Manuscripts",
    description:
      "A practical risk checklist for client authorization, confidentiality, data processing, retention, evidence review, and transparent AI-assisted fiction editing.",
    keywords: ["AI consent for editors", "AI client manuscript privacy", "AI tools for fiction editors"],
    heading: "How to use AI with client manuscripts without hiding the workflow",
    lede:
      "Professional use begins with authority and disclosure, not with the upload button. Confirm what the client permits, understand the data flow, and keep human verification visible in the deliverable.",
    summary:
      "This is an operational checklist, not legal advice. Adapt it to your contract, jurisdiction, professional association, insurer, and the sensitivity of the manuscript.",
    sections: [
      {
        heading: "Obtain specific authorization",
        paragraphs: [
          "Do not assume a general confidentiality clause permits third-party AI processing. Explain the purpose, provider, content being processed, retention, expected output, and whether the tool may use content for training.",
        ],
      },
      {
        heading: "Minimize what you send",
        paragraphs: [
          "Use only the manuscript content required for the agreed task. Remove unrelated personal data, verify that you selected the correct client project, and avoid pasting confidential prose into unapproved general chat tools.",
        ],
      },
      {
        heading: "Review every output",
        paragraphs: [
          "Treat AI findings as unverified leads. Check the cited passages, reject hallucinated relationships, distinguish narrator belief from story fact, and write the client-facing interpretation yourself.",
        ],
      },
      {
        heading: "Close the data lifecycle",
        paragraphs: [
          "Record the tool and processing date, retain only what the engagement requires, delete projects according to the agreement, and know how to respond if the client withdraws authorization or asks where their text was processed.",
        ],
      },
    ],
    related: [{ href: "/templates/editor-ai-consent-clause", label: "Editor AI-consent clause template" }, ...sharedRelated],
    ctaTitle: "Review Novilot’s processing boundary",
    ctaBody: "Understand the current data flow and limitations before importing a client-authorized manuscript.",
  },
];

export const EDITOR_TEMPLATE_PAGES: EditorContentPage[] = [
  {
    kind: "template",
    slug: "fiction-continuity-checklist",
    path: "/templates/fiction-continuity-checklist",
    title: "Fiction Continuity Checklist for Editors",
    description:
      "Copy a professional fiction continuity checklist covering characters, timeline, knowledge, relationships, objects, locations, and world rules.",
    keywords: ["fiction continuity checklist", "novel continuity checklist", "continuity editing template"],
    heading: "Fiction continuity checklist for editors",
    lede:
      "Use this checklist to structure a dedicated continuity pass. Remove categories outside the engagement and add genre-specific rules before the manuscript review begins.",
    summary: "The checklist is plain markdown and works in Word, Google Docs, Notion, Scrivener, or a project-management tool.",
    sections: [
      {
        heading: "Use evidence, not memory",
        paragraphs: [
          "Record the earlier and later passages for every suspected problem. The checklist organizes the review; the manuscript evidence determines whether a query is justified.",
        ],
      },
    ],
    templateSections: [
      { heading: "Scope and source", note: "Identify the exact draft being checked.", fields: ["Manuscript title and version", "Word count and chapters included", "Series material included", "Continuity-only or factual checks also in scope"] },
      { heading: "Characters", note: "Separate durable facts from changing states.", fields: ["Names, aliases, titles, and pronouns", "Age, appearance, injuries, and abilities", "Goals, allegiances, relationships, and possessions", "Knowledge, beliefs, secrets, and lies"] },
      { heading: "Time and place", note: "Check sequence as well as stated dates.", fields: ["Dates, seasons, time of day, and elapsed time", "Travel duration and distance", "Location layout and access", "Event order and simultaneous scenes"] },
      { heading: "World and plot rules", note: "Record constraints the solution must respect.", fields: ["Magic, technology, legal, social, or biological rules", "Object ownership and condition", "Promises, clues, evidence, and unresolved setup", "Rule exceptions established on the page"] },
      { heading: "Finding record", note: "Complete this for every reportable issue.", fields: ["Category and severity", "Passage A and location", "Passage B and location", "Why they may conflict", "Status: confirmed / intentional / query / dismiss", "Editor note and author question"] },
    ],
    related: [{ href: "/how-to/run-a-fiction-continuity-pass", label: "How to use the checklist" }, ...sharedRelated],
    ctaTitle: "Pair the checklist with manuscript evidence",
    ctaBody: "Run a Novilot continuity audit, verify the cited passages, and record only the findings you accept.",
  },
  {
    kind: "template",
    slug: "developmental-editorial-report",
    path: "/templates/developmental-editorial-report",
    title: "Developmental Editorial Report Template",
    description:
      "Copy a developmental editorial report template for scope, strengths, priorities, structure, character, pacing, continuity, revision plan, and author questions.",
    keywords: ["developmental editorial report template", "editorial letter template", "manuscript evaluation template"],
    heading: "Developmental editorial report template",
    lede:
      "A strong editorial report gives the author a hierarchy, not a transcript of every observation. Adapt this structure to the contracted edit and the manuscript’s needs.",
    summary: "The template separates evidence, interpretation, priority, and recommended next action so the author can plan the revision.",
    sections: [
      {
        heading: "Lead with the revision hierarchy",
        paragraphs: [
          "Start with the few changes that affect the most downstream work. Scene-level and sentence-level notes are harder to use when the story’s central structure or character logic is still moving.",
        ],
      },
    ],
    templateSections: [
      { heading: "Engagement and manuscript", note: "State what was reviewed and what was outside scope.", fields: ["Manuscript title and version", "Edit type and agreed scope", "Word count and materials reviewed", "Limitations and exclusions"] },
      { heading: "Executive assessment", note: "Give the author a usable overview.", fields: ["What the manuscript is trying to achieve", "What is already working", "Three highest-priority revision goals", "Recommended revision order"] },
      { heading: "Story and structure", note: "Connect observations to reader experience.", fields: ["Premise and central conflict", "Act or sequence progression", "Scene purpose and escalation", "Climax and resolution"] },
      { heading: "Character and point of view", note: "Distinguish arc, motivation, knowledge, and voice.", fields: ["Protagonist goal and agency", "Character arcs and relationships", "Point-of-view control", "Knowledge and revelation timing"] },
      { heading: "Pacing, continuity, and world", note: "Use cited examples for specific questions.", fields: ["Pacing pressure points", "Continuity findings with both passage locations", "Timeline and world-rule questions", "Patterns to watch during revision"] },
      { heading: "Revision plan and queries", note: "End with action and conversation.", fields: ["Revision sequence", "Questions requiring author intent", "Suggested checkpoints", "Materials returned with this report"] },
    ],
    related: [{ href: "/sample-continuity-report", label: "View a synthetic report example" }, ...sharedRelated],
    ctaTitle: "Ground report questions in the manuscript",
    ctaBody: "Use Novilot to locate possible evidence, then write the interpretation and revision priorities yourself.",
  },
  {
    kind: "template",
    slug: "client-manuscript-intake",
    path: "/templates/client-manuscript-intake",
    title: "Client Manuscript Intake Form for Fiction Editors",
    description:
      "Copy a fiction editor client-intake form covering manuscript version, genre, goals, scope, deliverables, AI authorization, privacy, and deadlines.",
    keywords: ["client manuscript intake form", "fiction editor intake form", "developmental editing questionnaire"],
    heading: "Client manuscript intake form",
    lede:
      "Good analysis begins with the correct draft, the correct scope, and a shared understanding of what the author wants the edit to accomplish.",
    summary: "Use this form before importing or analyzing a manuscript. Adapt contract and consent language with qualified professional advice.",
    sections: [
      {
        heading: "Keep intake separate from diagnosis",
        paragraphs: [
          "The intake form captures author goals and operational facts. It should not promise a diagnosis before the editor has read the manuscript or imply that software output determines the edit.",
        ],
      },
    ],
    templateSections: [
      { heading: "Author and manuscript", note: "Confirm the source material.", fields: ["Author name and preferred contact", "Manuscript title and version date", "Genre, audience, and approximate word count", "Series context or previous books"] },
      { heading: "Goals and concerns", note: "Let the author define the problem in their own terms.", fields: ["What do you want this edit to accomplish?", "Which parts feel strongest?", "Which parts concern you most?", "What feedback have you already received?"] },
      { heading: "Scope and deliverables", note: "Match the form to the contract.", fields: ["Type of edit", "Material included and excluded", "Editorial letter, inline notes, call, or other deliverables", "Deadline and revision schedule"] },
      { heading: "Confidentiality and tools", note: "Obtain specific authorization before processing.", fields: ["NDA or confidentiality requirements", "Third-party tools permitted", "AI-assisted analysis permitted: yes / no / discuss", "Retention and deletion requirements", "Sensitive or personal data present"] },
      { heading: "Handoff", note: "Prevent version confusion.", fields: ["Preferred file format", "Track Changes or comment expectations", "Style sheet supplied", "Correct final upload link and filename"] },
    ],
    related: [{ href: "/templates/editor-ai-consent-clause", label: "AI-consent clause template" }, ...sharedRelated],
    ctaTitle: "Import only after scope and authorization are clear",
    ctaBody: "Novilot’s editor path begins with the client-authorized manuscript and a defined analysis purpose.",
  },
  {
    kind: "template",
    slug: "editor-ai-consent-clause",
    path: "/templates/editor-ai-consent-clause",
    title: "AI Consent Clause Template for Editors",
    description:
      "A plain-language starting template for author consent to AI-assisted editorial analysis, covering purpose, provider, review, retention, and withdrawal.",
    keywords: ["AI consent clause for editors", "editor AI contract clause", "client consent AI manuscript"],
    heading: "AI-assistance consent clause for editors",
    lede:
      "This template is a drafting aid, not legal advice. It should be reviewed for your contract, jurisdiction, insurer, professional obligations, and the actual data practices of every tool you use.",
    summary: "Specific consent is clearer than a generic statement that the editor may use unspecified software.",
    sections: [
      {
        heading: "Explain the actual workflow",
        paragraphs: [
          "Replace bracketed language with the named provider, the manuscript material being processed, the purpose of processing, retention period, deletion process, and the role of human review. Do not promise privacy controls the provider does not offer.",
        ],
      },
    ],
    templateSections: [
      { heading: "Purpose", note: "Describe the limited editorial task.", fields: ["The Editor may use [named service] to assist with [continuity / structural pattern / other specific analysis].", "The tool's output is advisory and will be reviewed by the Editor before it informs any client-facing feedback."] },
      { heading: "Content and provider", note: "Name what leaves the editor's direct control.", fields: ["The material processed may include [full manuscript / selected chapters / excerpts].", "Processing is performed by [provider and relevant subprocessors or link to current list]."] },
      { heading: "Training, retention, and deletion", note: "Insert verified facts only.", fields: ["The provider states [verified training-use policy].", "Project data will be retained for [period] and deleted by [method or date].", "The Editor will retain only the records required by the engagement and applicable law."] },
      { heading: "Authorization and alternatives", note: "Make the choice meaningful.", fields: ["The Author authorizes the processing described above: yes / no.", "If authorization is withheld, the Editor will [describe non-AI alternative and any scope, timeline, or fee effect].", "The Author may ask questions or withdraw authorization before processing begins."] },
      { heading: "Human responsibility", note: "Preserve professional accountability.", fields: ["The Editor remains responsible for evaluating outputs and making editorial recommendations.", "The analysis is not exhaustive and does not replace the agreed professional edit."] },
    ],
    related: [{ href: "/how-to/use-ai-with-client-manuscripts", label: "AI-assisted manuscript workflow checklist" }, ...sharedRelated],
    ctaTitle: "Check the provider facts before naming Novilot",
    ctaBody: "Review Novilot’s current data-processing explanation, then adapt this draft with appropriate professional advice.",
  },
];

export const EDITOR_SECURITY_PAGES: EditorContentPage[] = [
  {
    kind: "security",
    slug: "editor-data-processing",
    path: "/security/editor-data-processing",
    title: "Client Manuscript Data and AI Processing - Novilot",
    description:
      "Understand Novilot’s current client-manuscript processing boundary: browser-side import, AI processing, storage, analytics masking, deletion, and editor responsibilities.",
    keywords: ["client manuscript AI privacy", "Novilot data processing", "AI editing confidentiality"],
    heading: "Know what happens before you import a client manuscript",
    lede:
      "Editors need a concrete processing boundary, not a general promise that AI is safe. This page summarizes the current product behavior and links to the controlling privacy and terms pages.",
    summary:
      "This page is operational guidance, not a DPA or legal opinion. If your client, contract, or jurisdiction requires additional terms, obtain them before processing.",
    sections: [
      {
        heading: "Import and storage",
        paragraphs: [
          "Supported manuscript files are parsed in the browser so the user can review the proposed chapter split before confirming import. Confirmed manuscript content and project data are stored so Novilot can provide the workspace, analysis, and later access described by the product.",
          "Authors can optionally attach the original file. It is stored for authorized downloads and may include chapters omitted from the text preview.",
          "The current product is not a local-only application. Do not represent it to a client as equivalent to an offline Word add-in.",
        ],
      },
      {
        heading: "AI processing",
        paragraphs: [
          "Relevant manuscript content is sent to configured AI providers when the user deliberately runs an AI-backed analysis. Novilot’s privacy page states that manuscript content is not used to train AI models. Editors should still disclose the provider-assisted workflow when their contract or professional obligations require it.",
        ],
      },
      {
        heading: "Analytics boundary",
        paragraphs: [
          "Novilot’s product analytics are configured to avoid manuscript prose: automatic text capture is disabled, session-replay text and inputs are masked, and manually captured events are limited to non-prose properties such as counts, IDs, booleans, and enums.",
        ],
      },
      {
        heading: "Deletion and editor responsibility",
        paragraphs: [
          "Editors can record upload permission, revoke review links, delete a project immediately, or schedule deletion from project data controls. Deleting a project removes its stored chapters, editorial snapshots, findings, and review links from the application database. Backup retention and AI-provider handling remain subject to the privacy policy; scheduled deletion is not a promise of immediate removal from backups or providers. Preserve the original client file outside Novilot as appropriate.",
          "Ending sharing stops new replies and revisions; authors keep read-only access to published feedback. Previously accepted or downloaded copies cannot be recalled.",
        ],
      },
    ],
    related: [
      { href: "/privacy", label: "Privacy policy" },
      { href: "/terms", label: "Terms of service" },
      { href: "/ai", label: "How Novilot uses AI" },
      { href: "/templates/editor-ai-consent-clause", label: "AI-consent clause template" },
    ],
    ctaTitle: "Use only a client-authorized manuscript",
    ctaBody: "Confirm that the current processing boundary fits the engagement before starting an editor preflight.",
  },
];

export const EDITOR_CONTENT_PAGES = [
  ...EDITOR_FEATURE_PAGES,
  ...EDITOR_COMPARISON_PAGES,
  ...EDITOR_GUIDE_PAGES,
  ...EDITOR_TEMPLATE_PAGES,
  ...EDITOR_SECURITY_PAGES,
  ...EDITOR_WORKFLOW_PAGES,
] as const;

/** Every editor-acquisition route published through all marketing locales. */
export const EDITOR_MARKETING_PATHS = [
  "/for/fiction-editors",
  "/sample-continuity-report",
  ...EDITOR_CONTENT_PAGES.map((page) => page.path),
] as const;

export function editorContentPage(kind: EditorContentKind, slug: string) {
  return EDITOR_CONTENT_PAGES.find((page) => page.kind === kind && page.slug === slug);
}

export function editorContentStaticParams(kind: EditorContentKind) {
  return EDITOR_CONTENT_PAGES.filter((page) => page.kind === kind).map((page) => ({
    slug: page.slug,
  }));
}
